import algoliasearch from 'algoliasearch/lite'
import { SearchResponse } from 'instantsearch.js'
import { useCallback, useEffect, useState } from 'react'
import { FirmAlgoliaSearchResult } from '../components/search/firm'
import { OfferAlgoliaSearchResult } from '../components/search/offer'
import { composeFirmAlgoliaResults, composeOfferAlgoliaResults } from '../services/search/composeAlgoliaResults'

export interface AlgoliaConfig {
  appId: string
  searchkey: string
  offersIndex: string
  firmsIndex: string
}

export interface AlgoliaSearchResult {
  objectID: string
  name: string
  type: 'offer' | 'firm'
  url: string
  description?: string
  location?: string
  highlightResult?: {
    name?: {
      value: string
    }
    description?: {
      value: string
    }
  }
  logo?: string
}

interface UseAlgoliaSearchProps {
  appId: string
  searchkey: string
  offersIndex: string
  firmsIndex: string
  debounceMs?: number
  /**
   * Custom function to compose offer search results
   * If not provided, uses the default composeOfferAlgoliaResults function
   */
  composeOffers?: (results: OfferAlgoliaSearchResult[]) => AlgoliaSearchResult[]
  /**
   * Custom function to compose firm search results
   * If not provided, uses the default composeFirmAlgoliaResults function
   */
  composeFirms?: (results: FirmAlgoliaSearchResult[]) => AlgoliaSearchResult[]
}

interface UseAlgoliaSearchReturn {
  results: AlgoliaSearchResult[]
  isLoading: boolean
  error: string | null
  search: (query: string) => void
  offers: AlgoliaSearchResult[]
  firms: AlgoliaSearchResult[]
  clearResults: () => void
}

/**
 * Custom hook for Algolia search with support for custom result composition
 * 
 * @example
 * ```tsx
 * // Using default composition functions
 * const { results, search, isLoading } = useAlgoliaSearch({
 *   appId: 'your-app-id',
 *   searchkey: 'your-search-key',
 *   offersIndex: 'offers',
 *   firmsIndex: 'firms',
 * })
 * 
 * // Using custom composition functions
 * const { results, search, isLoading } = useAlgoliaSearch({
 *   appId: 'your-app-id',
 *   searchkey: 'your-search-key',
 *   offersIndex: 'offers',
 *   firmsIndex: 'firms',
 *   composeOffers: (offers) => offers.map(offer => ({
 *     ...offer,
 *     name: `Custom: ${offer.title}`,
 *   })),
 *   composeFirms: (firms) => firms.map(firm => ({
 *     ...firm,
 *     name: `Custom: ${firm.name}`,
 *   })),
 * })
 * ```
 */
export const useAlgoliaSearch = ({
  appId,
  searchkey,
  offersIndex,
  firmsIndex,
  debounceMs = 300,
  composeOffers = composeOfferAlgoliaResults,
  composeFirms = composeFirmAlgoliaResults,
}: UseAlgoliaSearchProps): UseAlgoliaSearchReturn => {
  const [results, setResults] = useState<AlgoliaSearchResult[]>([])
  const [offers, setOffers] = useState<AlgoliaSearchResult[]>([])
  const [firms, setFirms] = useState<AlgoliaSearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null)

  const clearResults = useCallback(() => {
    setResults([])
    setError(null)
  }, [])

  const search = useCallback(
    (query: string) => {
      console.debug('Search called with query:', query)
      // Clear previous timeout
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }

      // Clear results if query is empty
      if (!query.trim()) {
        clearResults()
        return
      }

      // Set new timeout for debouncing
      const timeout = setTimeout(async () => {
        setIsLoading(true)
        setError(null)

        try {
          const searchClient = algoliasearch(appId, searchkey, {

          })

          const searchResponse = await searchClient.search([
            {
              indexName: offersIndex,
              params: {
                query,
                hitsPerPage: 10,
                highlightPreTag: '<mark>',
                highlightPostTag: '</mark>',
              },
            },
            {
              indexName: firmsIndex,
              params: {
                query,
                hitsPerPage: 10,
                highlightPreTag: '<mark>',
                highlightPostTag: '</mark>',
              },
            },
          ])

          const offersResult = composeOffers((searchResponse.results[0] as SearchResponse<OfferAlgoliaSearchResult>).hits || [])
          const firmsResult = composeFirms((searchResponse.results[1] as SearchResponse<FirmAlgoliaSearchResult>).hits || [])

          setResults([...offersResult, ...firmsResult])
          setOffers(offersResult)
          setFirms(firmsResult)
        } catch (err) {
          console.error('Search error:', err)
          setError(err instanceof Error ? err.message : 'Search failed')
          setResults([])
        } finally {
          setIsLoading(false)
        }
      }, debounceMs)

      setDebounceTimeout(timeout)
    },
    [appId, searchkey, offersIndex, firmsIndex, debounceMs, clearResults, composeOffers, composeFirms]
  )

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }
  }, [debounceTimeout])

  return {
    results,
    offers,
    firms,
    isLoading,
    error,
    search,
    clearResults,
  }
} 
